import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

const EditModeContext = createContext(null);

// Map a pathname to a layout key. Dynamic routes share one layout per type.
const STATIC_PAGE_KEYS = {
  "/": "home", "/about": "about", "/events": "events", "/holidays": "holidays",
  "/classes": "classes", "/kosher": "kosher", "/services": "services",
  "/kosher-establishments": "kosher-establishments", "/tourist-desk": "tourist-desk",
};
function pathToPageKey(pathname) {
  if (STATIC_PAGE_KEYS[pathname]) return STATIC_PAGE_KEYS[pathname];
  if (pathname.startsWith("/programs/")) return "program-detail";
  if (pathname.startsWith("/events/")) return "event-detail";
  if (pathname.startsWith("/holidays/")) return "holiday-detail";
  return null;
}

export function EditModeProvider({ children }) {
  const location = useLocation();
  const { overrides, refreshOverrides, lang, setLang } = useLang();

  const params = new URLSearchParams(location.search);
  const hasToken = typeof window !== "undefined" && !!localStorage.getItem("cph_token");
  const editing = params.get("edit") === "1" && hasToken;
  const pageKey = pathToPageKey(location.pathname);

  // In edit mode the "edit language" IS the page's display language, so you always
  // see and edit text in full context. Switching it switches the whole page.
  const editLang = lang;
  const setEditLang = setLang;
  const [layout, setLayout] = useState({});
  const [saving, setSaving] = useState(false);

  // Layout is fetched for everyone so hidden/reordered sections apply on the public site too.
  useEffect(() => {
    api.get("/page-layout").then((r) => setLayout(r.data || {})).catch(() => {});
  }, []);

  const pageLayout = layout[pageKey] || {};

  const persistLayout = useCallback(async (next) => {
    setLayout(next);
    setSaving(true);
    try { await api.put("/page-layout", { layout: next }); }
    catch { toast.error("Could not save layout"); }
    finally { setSaving(false); }
  }, []);

  const updatePage = useCallback((mut) => {
    const cur = layout[pageKey] || {};
    const nextPage = mut({ hidden: {}, blocks: [], order: null, ...cur });
    persistLayout({ ...layout, [pageKey]: nextPage });
  }, [layout, pageKey, persistLayout]);

  const isHidden = useCallback((id) => !!(pageLayout.hidden && pageLayout.hidden[id]), [pageLayout]);
  const toggleHidden = (id) => updatePage((p) => ({ ...p, hidden: { ...p.hidden, [id]: !p.hidden?.[id] } }));

  const getOrder = useCallback((defaultOrder) => {
    const saved = pageLayout.order;
    if (!saved || !saved.length) return defaultOrder;
    const merged = saved.filter((id) => defaultOrder.includes(id));
    defaultOrder.forEach((id) => { if (!merged.includes(id)) merged.push(id); });
    return merged;
  }, [pageLayout]);

  const moveSection = (order, id, dir) => {
    const arr = order.slice();
    const i = arr.indexOf(id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    updatePage((p) => ({ ...p, order: arr }));
  };

  const blocks = pageLayout.blocks || [];
  const addTextBlock = (afterId) => {
    const id = "block-" + Math.random().toString(16).slice(2, 8);
    updatePage((p) => ({ ...p, blocks: [...(p.blocks || []), { id, after: afterId, i18n: {} }] }));
    toast.success("Text section added — click it to write your content");
  };
  const updateBlock = (id, lang, patch) => updatePage((p) => ({
    ...p,
    blocks: (p.blocks || []).map((b) => (b.id === id ? { ...b, i18n: { ...b.i18n, [lang]: { ...(b.i18n?.[lang] || {}), ...patch } } } : b)),
  }));
  const removeBlock = (id) => updatePage((p) => ({ ...p, blocks: (p.blocks || []).filter((b) => b.id !== id) }));

  const saveText = useCallback(async (key, value) => {
    const body = {
      en: { ...overrides.en }, cs: { ...overrides.cs },
      he: { ...overrides.he }, ru: { ...overrides.ru },
    };
    body[editLang] = { ...body[editLang], [key]: value };
    setSaving(true);
    try {
      await api.put("/site-text", body);
      await refreshOverrides();
      toast.success("Saved");
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  }, [overrides, editLang, refreshOverrides]);

  const value = {
    editing, editLang, setEditLang, pageKey, saving, saveText,
    isHidden, toggleHidden, getOrder, moveSection,
    blocks, addTextBlock, updateBlock, removeBlock,
  };
  return <EditModeContext.Provider value={value}>{children}</EditModeContext.Provider>;
}

export function useEditMode() {
  return useContext(EditModeContext) || { editing: false, isHidden: () => false, getOrder: (d) => d, blocks: [] };
}
